import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import lightBackground from '../src/assets/wa-background-light.png';
import darkBackground from '../src/assets/wa-background-dark.jpg';
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';

import Routes from "./routes";
import { openApi } from "./services/api";
import { CookieProvider } from "./context/CookieContext";

const queryClient = new QueryClient();

const App = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                    backgroundColor: mode === "light" ? "#3ecc96" : "#066e4a",
					borderRadius: "8px",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#f1f1f1" : "#373737",
					borderRadius: "8px",
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "#3ecc96" : "#066e4a" },
                quicktags: { main: mode === "light" ? "#3ecc96" : "#066e4a" },
                sair: { main: mode === "light" ? "#df3d33" : "#df3d33" },
                vcard: { main: mode === "light" ? "#3ecc96" : "#666" },
                textPrimary: mode === "light" ? "#2d2d2d" : "#e3e6e8",
                borderPrimary: mode === "light" ? "#3ecc96" : "#066e4a",
                dark: { main: mode === "light" ? "#171717" : "#fcfcfc" },
                light: { main: mode === "light" ? "#fcfcfc" : "#171717" },
                tabHeaderBackground: mode === "light" ? "#f1f1f1" : "#2d2d2d",
                ticketlist: mode === "light" ? "#fcfcfc" : "#171717",
                optionsBackground: mode === "light" ? "#fcfcfc" : "#2d2d2d",
				options: mode === "light" ? "#fcfcfc" : "#373737",
                fontecor: mode === "light" ? "#3ecc96" : "#e3e6e8",
                fancyBackground: mode === "light" ? "#fcfcfc" : "#171717",
				bordabox: mode === "light" ? "#e6e6e6" : "#434343",
				newmessagebox: mode === "light" ? "#f1f1f1" : "#2d2d2d",
				inputdigita: mode === "light" ? "#f8f8f8" : "#3c3c3c",
				contactdrawer: mode === "light" ? "#fcfcfc" : "#2d2d2d",
				announcements: mode === "light" ? "#f1f1f1" : "#2d2d2d",
				login: mode === "light" ? "#fcfcfc" : "#171717",
				announcementspopover: mode === "light" ? "#fcfcfc" : "#3c3c3c",
				chatlist: mode === "light" ? "#f1f1f1" : "#2d2d2d",
				boxlist: mode === "light" ? "#f1f1f1" : "#2d2d2d",
				boxchatlist: mode === "light" ? "#f1f1f1" : "#171717",
                total: mode === "light" ? "#fcfcfc" : "#222",
                messageIcons: mode === "light" ? "grey" : "#e3e6e8",
                inputBackground: mode === "light" ? "#fcfcfc" : "#3c3c3c",
                barraSuperior: mode === "light" ? "linear-gradient(to right, #3ecc96, #2de195 , #3ecc96)" : "#066e4a",
				boxticket: mode === "light" ? "#f1f1f1" : "#2d2d2d",
				campaigntab: mode === "light" ? "#f1f1f1" : "#2d2d2d",
				mediainput: mode === "light" ? "#f1f1f1" : "#171717",
				contadordash: mode == "light" ? "#3ecc96" : "#066e4a",
            },
            typography: {
                fontFamily: 'Outfit, sans-serif',
            },
            mode,
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale =
            i18nlocale ? i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5) : "ptBR";

        if (browserLocale === "ptBR") {
            setLocale(ptBR);
        }
    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
        document.documentElement.setAttribute("data-theme", mode);
    }, [mode]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                    <CookieProvider>
                      <Routes />
                    </CookieProvider>
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
